import React from 'react';
import PropTypes from 'prop-types';

import { get } from 'dpl/shared/utils/object';
import SmartImage from 'dpl/components/SmartImage';
import { StructuredTextPropTypes } from 'dpl/prismic/utils/customPropTypes';
import RichText from 'dpl/prismic/components/RichText';
import { getArticlePath } from 'dpl/prismic/utils';

import Byline from './Byline';
import Breadcrumb from './Breadcrumb';
import HeaderFeature from '../header_feature/components/HeaderFeature';

export default function ArticleHeader({ article, sliceData }) {
  const publicationDate = article.last_publication_date;
  const hubPageTitle = get(article, 'data.hub_page.data.title.0.text');
  const heroImage = get(sliceData, 'primary.hero_image');
  const heroImageCaption = get(sliceData, 'primary.hero_image_caption');
  const introText = get(sliceData, 'primary.intro_text') || [];
  const feature = get(sliceData, 'primary.header_feature');
  const authors = article.data.authors.map(f => f.author.data).filter(Boolean);

  const hasIntroText = introText.length > 0 && introText[0].text.length > 0;

  return (
    <div className="PrismicArticleHeader container">
      <div className="row flex-nowrap">
        <div className="col">
          {(hubPageTitle || publicationDate) && (
            <div className="mb4 mb7-md items-center">
              <Breadcrumb
                title={hubPageTitle}
                path={getArticlePath(article.data.hub_page)}
                publicationDate={publicationDate}
              />
            </div>
          )}
          <div className="flex justify-between">
            <div>
              {article.data.title && (
                <div className="PrismicArticleHeader__title mb2 mb4-md">
                  <RichText content={article.data.title} />
                </div>
              )}
              {article.data.deck && (
                <div className="PrismicArticleHeader__deck mb6">
                  <RichText content={article.data.deck} />
                </div>
              )}
              {authors.length > 0 && <Byline authors={authors} />}
            </div>
            {feature && (
              <div className="col-4 d-xl-block d-none ml14 mb8">
                <div className="bl bw2 b--purple pl6 pv4">
                  <HeaderFeature feature={feature} sliceData={sliceData} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {heroImage && heroImage.url && (
        <div className="mt8 row">
          <div className="col-xl-9">
            <div className="aspect-ratio aspect-ratio--6x4 f0">
              <div className="aspect-ratio--object">
                <SmartImage
                  tag="div"
                  src={heroImage.url}
                  alt={heroImage.alt}
                  lazy
                  className="w-100 h-100 bg-center cover"
                />
              </div>
            </div>
            {heroImageCaption && (
              <div className="PrismicArticleHeader__heroImageCaption mt3">
                <RichText content={heroImageCaption} />
              </div>
            )}
          </div>
        </div>
      )}
      {feature && (
        <div className="d-block d-lg-none mt8 mb8 min-w-100">
          <div className="bg-lightest-blue pv8 ph8">
            <HeaderFeature feature={feature} sliceData={sliceData} />
          </div>
        </div>
      )}
      {(hasIntroText || feature) && (
        <div className="row">
          <div className="flex offset-xl-1 col-xl-7">
            {hasIntroText && (
              <div className="PrismicArticleHeader__introText i mt8">
                <RichText content={introText} />
              </div>
            )}
            {feature &&
              (hasIntroText ? (
                <div className="col-4 ml16 mt8 d-lg-block d-none d-xl-none">
                  <div className="bl bw2 b--purple pl5 pv4">
                    <HeaderFeature feature={feature} sliceData={sliceData} />
                  </div>
                </div>
              ) : (
                <div className="d-lg-block d-none d-xl-none mt8 min-w-100">
                  <div className="bg-lightest-blue pv8 ph8">
                    <HeaderFeature feature={feature} sliceData={sliceData} />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

ArticleHeader.propTypes = {
  article: PropTypes.shape({
    title: StructuredTextPropTypes,
    deck: StructuredTextPropTypes,
    _meta: PropTypes.shape({
      lastPublicationDate: PropTypes.string
    }),
    data: PropTypes.shape({
      authors: PropTypes.arrayOf(
        PropTypes.shape({
          full_name: PropTypes.string,
          avatar: PropTypes.shape({
            url: PropTypes.string
          }),
          href: PropTypes.string
        })
      ),
      title: StructuredTextPropTypes,
      deck: StructuredTextPropTypes,
      hub_page: PropTypes.shape({
        title: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string })),
        uid: PropTypes.string
      })
    }),
    last_publication_date: PropTypes.string,
    hub_page: PropTypes.shape({
      title: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string })),
      uid: PropTypes.string
    })
  }).isRequired,
  sliceData: PropTypes.shape({
    primary: PropTypes.shape({
      hero_image: PropTypes.shape({
        alt: PropTypes.string,
        url: PropTypes.string
      }),
      hero_image_caption: StructuredTextPropTypes,
      intro_text: StructuredTextPropTypes
    })
  })
};

ArticleHeader.defaultProps = {
  sliceData: {}
};
